import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const DISTRICT_SEARCH_FIELDS = ['name', 'zipcode'];
const DISTRICT_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (d) => d.name?.toLowerCase() ?? '' },
];



const DistrictComponent = () => {
  const [districts, setDistricts] = useState([]);
  const [currentDistrict, setCurrentDistrict] = useState({
    name: '',
    schools: '',
    zipcode: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [districtToDelete, setDistrictToDelete] = useState(null);
  const [schools, setSchools] = useState([]);
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);

  const {
    items: visibleDistricts,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(districts, {
    searchFields: DISTRICT_SEARCH_FIELDS,
    sortOptions: DISTRICT_SORT_OPTIONS,
  });



  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all districts
  useEffect(() => {
    fetchDistricts();
    fetchSchools();
  }, []);

  const fetchDistricts = async () => {
    const response = await fetch(`${backendURL}/api/districts`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setDistricts(data);
  };

  const handleFormChange = (e) => {
    setCurrentDistrict({ ...currentDistrict, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if name is empty
    if (!currentDistrict.name || currentDistrict.name.trim() === '') {
      setShowNameModal(true); // Show the modal if no name
      return; // Stop further execution
    }

    // Check if zipcode is empty
    if (!currentDistrict.zipcode || currentDistrict.zipcode.trim() === '') {
      setShowZipCodeModal(true); // Show the modal if no zipcode
      return; // Stop further execution
    }

    if (currentDistrict.district_id) {
      await updateDistrict(currentDistrict.district_id, currentDistrict);
    } else {
      await createDistrict(currentDistrict);
    }
    setCurrentDistrict({ name: '', schools: '', zipcode: '' });
    setShowForm(false);
    fetchDistricts();
  };



  const fetchSchools = async () => {
    try {
      const response = await fetch(`${backendURL}/api/schools`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      // Check if the data contains the 'schools' property
      if (data) {
        setSchools(data);

      } else {
        // Handle cases where 'schools' is not in the response
        console.error('Schools data is not available in the response');
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
      // Handle error scenario
    }
  };

  const createDistrict = async (districtData) => {
    await fetch(`${backendURL}/api/districts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(districtData),
      credentials: 'include'
    });
  };

  const updateDistrict = async (districtId, districtData) => {
    await fetch(`${backendURL}/api/districts/${districtId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(districtData),
      credentials: 'include'
    });
  };

  const deleteDistrict = async () => {
    if (districtToDelete) {
      await fetch(`${backendURL}/api/districts/${districtToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setDistrictToDelete(null);
      setShowModal(false);
      fetchDistricts();
    }
  };

  const editDistrict = (district) => {
    setCurrentDistrict(district);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentDistrict({ name: '', schools: '', zipcode: '' });
    setShowForm(false);
  }

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentDistrict({ name: '', schools: '', zipcode: '' });
    }
    setShowForm(!showForm);
  };

  const countSchoolsForDistrict = (districtId) => {
    return schools.filter(school => school.district_id === districtId).length;
  };

  const districtForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter district name"
            name="name"
            onChange={handleFormChange}
            value={currentDistrict.name}
          />
        </Form.Group>
        <Form.Group controlId="zipcode">
          <Form.Label>Zipcode</Form.Label>
          <Form.Control
            type="number"
            placeholder="Enter zipcode"
            name="zipcode"
            onChange={handleFormChange}
            value={currentDistrict.zipcode}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New District"
        listLabel="Show Districts List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Districts"
        form={districtForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="district"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search districts" placeholder="Name or zipcode" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No districts have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleDistricts.map((district) => (
          <ExpandableRow
            key={district.district_id}
            main={{ title: district.name, subtitle: `Zip: ${district.zipcode || 'N/A'}` }}
            pills={[{ label: `${countSchoolsForDistrict(district.district_id)} schools` }]}
            actions={(
              <>
                <Button size="sm" onClick={() => editDistrict(district)} aria-label="Edit district">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDistrictToDelete(district.district_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete district"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteDistrict}
        bodyText="Are you sure you want to delete this district?"
      />
      <Modal show={showZipCodeModal} onHide={() => setShowZipCodeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Missing Zip Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a zip code for the district.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowZipCodeModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showNameModal} onHide={() => setShowNameModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Missing Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a name for the new district.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNameModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default DistrictComponent;
